import React from 'react';
import { XIcon } from 'lucide-react';
interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyPolicyModal({
  isOpen,
  onClose
}: PrivacyPolicyModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-id="element-220">
      <div className="bg-slate border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl relative" onClick={e => e.stopPropagation()} data-id="element-221">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-slate/95 backdrop-blur z-10 rounded-t-2xl" data-id="element-222">
          <h2 className="text-2xl font-bold text-white" data-id="element-223">Privacy Policy</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/5 text-gray-400 hover:text-white transition-colors" aria-label="Close modal" data-id="element-224">
            <XIcon className="w-6 h-6" data-id="element-225" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 md:p-8 overflow-y-auto text-gray-300 space-y-8 text-sm md:text-base leading-relaxed" data-id="element-226">
          <section data-id="element-227">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-228">
              Welcome to FIQIS
            </h3>
            <p className="mb-4" data-id="element-229">
              We take your privacy very seriously. Please read this privacy
              policy carefully as it contains important information on who we
              are and our information practices, meaning how and why we collect,
              use, disclose, sell, share, store, and retain your personal
              information. It also explains your rights in relation to your
              personal information and how to contact us or supervisory
              authorities in the event you have a complaint or request.
            </p>
            <p className="mb-4" data-id="element-230">
              We collect your data as part of the service. By using our service
              and purchasing our product, you agree to the collection and use of
              information in accordance with this policy. Unless otherwise
              defined in this Privacy Policy, terms used in this Privacy Policy
              have the same meanings as in our Terms and Conditions, accessible
              from [T&C URL].
            </p>
            <p data-id="element-231">
              We collect, use, and are responsible for certain personal
              information about you. For California consumers, we are subject to
              the California Consumer Privacy Act of 2018 (CCPA), as amended by
              the California Privacy Rights Act of 2020 (CPRA). We are
              responsible as a "controller" of that personal information for the
              purposes of the GDPR. We are responsible for your personal
              information as a "business" under the CCPA/CPRA.
            </p>
          </section>

          <section data-id="element-232">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-233">
              1. Key Terms
            </h3>
            <p className="mb-4" data-id="element-234">
              It would be helpful to start by explaining some key terms used in
              this policy:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-navy/50 p-4 rounded-lg border border-white/5" data-id="element-235">
              <div className="font-semibold text-teal" data-id="element-236">We, us, our</div>
              <div className="md:col-span-2" data-id="element-237">FIQIS Inc.</div>

              <div className="font-semibold text-teal" data-id="element-238">
                Data protection officer contact
              </div>
              <div className="md:col-span-2" data-id="element-239">[email address]</div>

              <div className="font-semibold text-teal" data-id="element-240">
                Personal information
              </div>
              <div className="md:col-span-2" data-id="element-241">
                Any information relating to an identified or identifiable
                individual.
              </div>

              <div className="font-semibold text-teal" data-id="element-242">
                Special category personal information
              </div>
              <div className="md:col-span-2" data-id="element-243">
                Personal information revealing racial or ethnic origin,
                political opinions, religious beliefs, philosophical beliefs, or
                trade union membership; genetic and biometric data; and data
                concerning health, sex life or sexual orientation.
              </div>

              <div className="font-semibold text-teal" data-id="element-244">
                Sensitive Personal Information
              </div>
              <div className="md:col-span-2" data-id="element-245">
                Personal information revealing a consumer's social security
                number, driver's license and passport numbers, account numbers
                and credentials, precise geolocation, racial or ethnic origin,
                religious beliefs, or union membership, personal information
                concerning a consumer's health, sex life, or sexual orientation,
                contents of a consumer's mail, email and text messages where the
                business is not the intended recipient, genetic data, biometric
                information, or citizenship and immigration status.
              </div>

              <div className="font-semibold text-teal" data-id="element-246">
                Biometric Information
              </div>
              <div className="md:col-span-2" data-id="element-247">
                An individual's physiological, biological, or behavioral
                characteristics, including information pertaining to an
                individual's deoxyribonucleic acid (DNA), that is used or is
                intended to be used singly or in combination with each other or
                with other identifying data, to establish individual identity.
                Biometric information includes, but is not limited to, imagery
                of the iris, retina, fingerprint, face, hand, palm, vein
                patterns, and voice recordings, from which an identifier
                template, such as a faceprint, a minutiae template, or a
                voiceprint, can be extracted, and keystroke patterns or rhythms,
                gait patterns or rhythms, and sleep, health, or exercise data
                that contain identifying information.
              </div>
            </div>
          </section>

          <section data-id="element-248">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-249">
              2. Personal Information We Collect About You
            </h3>
            <p className="mb-4" data-id="element-250">
              We may collect and use the following personal information,
              including sensitive personal information, that identifies, relates
              to, describes, is reasonable capable of being associated with, or
              could reasonably be linked, directly or indirectly, with a
              particular consumer or household:
            </p>

            <div className="overflow-x-auto" data-id="element-251">
              <table className="w-full text-left border-collapse" data-id="element-252">
                <thead data-id="element-253">
                  <tr className="border-b border-white/10 text-white" data-id="element-254">
                    <th className="py-3 pr-4 font-semibold" data-id="element-255">Categories</th>
                    <th className="py-3 px-4 font-semibold" data-id="element-256">Examples</th>
                    <th className="py-3 pl-4 font-semibold whitespace-nowrap" data-id="element-257">
                      Collected in Last 12 Months?
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5" data-id="element-258">
                  <tr data-id="element-259">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-260">
                      Identifiers
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-261">
                      A real name, alias, postal address, unique personal
                      identifier, online identifier, Internet Protocol address,
                      email address, account name, social security number,
                      driver's license number, passport number, or other similar
                      identifiers.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-262">Yes</td>
                  </tr>
                  <tr data-id="element-263">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-264">
                      Personal information described in the California Customer
                      Records Statute
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-265">
                      A signature, social security number, physical
                      characteristics or description, address, telephone number,
                      passport number, driver's license or state identification
                      card number, insurance policy number, education,
                      employment, employment history, bank account number,
                      credit card number, debit card number, or any other
                      financial information, medical information, or health
                      insurance information.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-266">Yes</td>
                  </tr>
                  <tr data-id="element-267">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-268">
                      Characteristics of protected classifications under
                      California or federal law
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-269">
                      Age (40 years or older), race, color, ancestry, national
                      origin, citizenship, religion or creed, marital status,
                      medical condition, physical or mental disability, sex
                      (including gender, gender identity, gender expression,
                      pregnancy or childbirth and related medical conditions),
                      sexual orientation, veteran or military status, genetic
                      information (including familial genetic information).
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-270">No</td>
                  </tr>
                  <tr data-id="element-271">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-272">
                      Commercial information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-273">
                      Records of personal property, products or services
                      purchased, obtained, or considered, or other purchasing or
                      consuming histories or tendencies.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-274">No</td>
                  </tr>
                  <tr data-id="element-275">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-276">
                      Biometric information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-277">
                      Physiological, biological, or behavioral characteristics,
                      DNA information. Imagery of the iris, retina, fingerprint,
                      face, hand, palm, vein patterns, or voice recordings.
                      Keystroke patterns or rhythms, gait patterns or rhythms,
                      or sleep, health, or exercise data.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-278">No</td>
                  </tr>
                  <tr data-id="element-279">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-280">
                      Internet and network activity
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-281">
                      Browsing history, search history, and information
                      regarding a consumer's interaction with an internet
                      website application, or advertisement.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-282">Yes</td>
                  </tr>
                  <tr data-id="element-283">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-284">
                      Geolocation data
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-285">
                      Information about physical locations or movements.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-286">Yes</td>
                  </tr>
                  <tr data-id="element-287">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-288">
                      Sensory information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-289">
                      Audio, electronic, visual, thermal, olfactory, or similar
                      information.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-290">No</td>
                  </tr>
                  <tr data-id="element-291">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-292">
                      Professional or employment-related information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-293">
                      Current or past job history or performance evaluations.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-294">No</td>
                  </tr>
                  <tr data-id="element-295">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-296">
                      Non-public education information
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-297">
                      Non-public education records that are directly related to
                      a student which are maintained by an educational agency or
                      institution or by a party acting on its behalf.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-298">No</td>
                  </tr>
                  <tr data-id="element-299">
                    <td className="py-3 pr-4 font-medium text-teal align-top" data-id="element-300">
                      Inferences drawn from other Personal Data
                    </td>
                    <td className="py-3 px-4 align-top" data-id="element-301">
                      Information used to create a user profile which reflects
                      the users' preferences, characteristics, psychological
                      trends, predispositions, behavior, attitudes,
                      intelligence, abilities, or aptitudes.
                    </td>
                    <td className="py-3 pl-4 align-top" data-id="element-302">No</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 italic" data-id="element-303">
              If you do not provide personal information required to provide
              service and/or product to you, it may delay or prevent us from
              providing services and/or product to you.
            </p>
          </section>

          <section data-id="element-304">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-305">
              3. How Your Personal Information is Collected
            </h3>
            <p className="mb-4" data-id="element-306">
              We collect personal information from the following categories of
              sources:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-307">
              <li data-id="element-308">
                You, directly in person, by telephone, text, or email and/or via
                our website
              </li>
              <li data-id="element-309">Third party with your consent (e.g., your bank)</li>
              <li data-id="element-310">Advertising networks</li>
              <li data-id="element-311">Internet service providers</li>
              <li data-id="element-312">Data analytics providers</li>
              <li data-id="element-313">Government entities</li>
              <li data-id="element-314">Operating systems and platforms</li>
              <li data-id="element-315">Social networks</li>
              <li data-id="element-316">Data brokers</li>
              <li data-id="element-317">Publicly accessible sources (e.g., property records)</li>
              <li data-id="element-318">Cookies on our website</li>
              <li data-id="element-319">
                Our IT and security systems, including automated monitoring of
                our websites and other technical systems, such as our computer
                networks and connections, CCTV and access control systems,
                communications systems, email, and instant messaging systems.
              </li>
            </ul>
          </section>

          <section data-id="element-320">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-321">
              4. How and Why We Use Your Personal Information
            </h3>
            <p className="mb-4" data-id="element-322">
              Under data protection laws, we can only use your personal
              information if we have a proper reason for doing so, for example:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-323">
              <li data-id="element-324">To comply with our legal and regulatory obligations</li>
              <li data-id="element-325">
                For the performance of our contract with you or to take steps at
                your request before entering into a contract
              </li>
              <li data-id="element-326">
                For our legitimate interests or those of a third party –or–
              </li>
              <li data-id="element-327">Where you have given consent</li>
            </ul>
            <p className="mb-4" data-id="element-328">
              A legitimate interest is when we have a business or commercial
              reason to use your information, so long as this is not overridden
              by your own rights and interests.
            </p>
            <p className="mb-4" data-id="element-329">
              We collect the categories of Personal Data listed above for the
              following business purposes:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-330">
              <li data-id="element-331">
                To take steps at your request before entering into a contract
                for services and/or products
              </li>
              <li data-id="element-332">
                To provide the services and/or products to you for their
                intended purposes.
              </li>
              <li data-id="element-333">
                For our legitimate interests or those of a third party, i.e., to
                minimize fraud that could be damaging for us and for you.
              </li>
              <li data-id="element-334">
                To send communications to you, including marketing
                communications (unless you opt-out).
              </li>
              <li data-id="element-335">
                To accomplish the purposes outlined in our Terms of Service.
              </li>
              <li data-id="element-336">
                To process your payments if you make a purchase through the
                Services, including sharing your payment information with third
                party payment processors. We will not store or collect your
                payment card details. That information is provided directly to
                our third-party payment processors whose use of your personal
                information is governed by their own Privacy Policy. These
                payment processors adhere to the standards set by PCI-DSS as
                managed by the PCI Security Standards Council.
              </li>
              <li data-id="element-337">To comply with our legal and regulatory obligations.</li>
            </ul>
          </section>

          <section data-id="element-338">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-339">
              5. Who We Share Your Personal Information With
            </h3>
            <p className="mb-4" data-id="element-340">
              We routinely share personal information with:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-341">
              <li data-id="element-342">
                Our affiliates, including companies within the FiQIS group
              </li>
              <li data-id="element-343">
                Service providers we use to help deliver our products and/or
                services to you, such as payment service providers, warehouses,
                and delivery companies
              </li>
              <li data-id="element-344">
                Other third parties we use to help us run our business, such as
                marketing agencies or website hosts
              </li>
              <li data-id="element-345">
                Third parties approved by you, including social media sites you
                choose to link your account to or third-party payment providers
              </li>
            </ul>
            <p className="mb-4" data-id="element-346">
              We only allow our service providers to handle your personal
              information if we are satisfied they take appropriate measures to
              protect your personal information. We also impose contractual
              obligations on service providers relating to ensure they can only
              use your personal information to provide services to us and to
              you. We may also share personal information with external
              auditors, e.g., in relation to ISO accreditation and the audit of
              our accounts.
            </p>
            <p className="mb-4" data-id="element-347">
              We may disclose and exchange information with law enforcement
              agencies and regulatory bodies to comply with our legal and
              regulatory obligations.
            </p>
            <p data-id="element-348">
              We may also need to share some personal information with other
              parties, such as potential buyers of some or all of our business
              or during a re-structuring. We will typically anonymize
              information, but this may not always be possible. The recipient of
              the information will be bound by confidentiality obligations.
            </p>
          </section>

          <section data-id="element-349">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-350">
              6. Personal Information We Sold or Shared
            </h3>
            <p className="mb-4" data-id="element-351">
              We will not sell your Personal Information to any third parties.
              Notwithstanding the preceding statement, in the preceding 12
              months, we have shared the following categories of personal
              information:
            </p>
            <div className="overflow-x-auto" data-id="element-352">
              <table className="w-full text-left border-collapse" data-id="element-353">
                <thead data-id="element-354">
                  <tr className="border-b border-white/10 text-white" data-id="element-355">
                    <th className="py-3 pr-4 font-semibold" data-id="element-356">Categories</th>
                    <th className="py-3 px-4 font-semibold" data-id="element-357">
                      Shared in last 12 months?
                    </th>
                    <th className="py-3 pl-4 font-semibold" data-id="element-358">Recipients</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5" data-id="element-359">
                  <tr data-id="element-360">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-361">
                      Identifiers
                    </td>
                    <td className="py-3 px-4" data-id="element-362">Yes</td>
                    <td className="py-3 pl-4" data-id="element-363">Affiliates, Service Providers</td>
                  </tr>
                  <tr data-id="element-364">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-365">
                      Personal information (CA Customer Records Statute)
                    </td>
                    <td className="py-3 px-4" data-id="element-366">Yes</td>
                    <td className="py-3 pl-4" data-id="element-367">Affiliates, Service Providers</td>
                  </tr>
                  <tr data-id="element-368">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-369">
                      Internet and network activity
                    </td>
                    <td className="py-3 px-4" data-id="element-370">Yes</td>
                    <td className="py-3 pl-4" data-id="element-371">Affiliates, Service Providers</td>
                  </tr>
                  <tr data-id="element-372">
                    <td className="py-3 pr-4 font-medium text-teal" data-id="element-373">
                      Geolocation data
                    </td>
                    <td className="py-3 px-4" data-id="element-374">Yes</td>
                    <td className="py-3 pl-4" data-id="element-375">Yes</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section data-id="element-376">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-377">
              7. Categories of Personal Information We Disclosed for a Business
              Purpose
            </h3>
            <p className="mb-4" data-id="element-378">
              In the preceding 12 months, we have disclosed the following
              categories of personal information for a business purpose:
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-379">
              <li data-id="element-380">
                Identifiers (e.g., a real name, alias, postal address, unique
                personal identifier, online identifier, Internet Protocol
                address, email address, account name, social security number,
                driver's license number, passport number, or other similar
                identifiers)
              </li>
              <li data-id="element-381">
                Information that identifies, relates to, describes, or is
                capable of being associated with, a particular individual,
                including, but not limited to, his or her name, signature,
                social security number, physical characteristics or description,
                address, telephone number, passport number, driver's license or
                state identification card number, insurance policy number,
                education, employment, employment history, bank account number,
                credit card number, debit card number, or any other financial
                information, medical information, or health insurance
                information
              </li>
              <li data-id="element-382">
                Internet or other electronic network activity information (e.g.,
                browsing history, search history, and information regarding a
                consumer's interaction with an Internet Web site, application,
                or advertisement)
              </li>
            </ul>
          </section>

          <section data-id="element-383">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-384">
              8. How Long Your Personal Information Will Be Kept
            </h3>
            <p className="mb-4" data-id="element-385">
              We will keep your personal information while you have an account
              with us or while we are providing products and/or services to you.
              Thereafter, we will keep your personal information for as long as
              is necessary:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-386">
              <li data-id="element-387">
                To respond to any questions, complaints or claims made by you or
                on your behalf
              </li>
              <li data-id="element-388">To show that we treated you fairly –or–</li>
              <li data-id="element-389">To keep records required by law</li>
            </ul>
            <p data-id="element-390">
              We will not retain your personal information for longer than
              necessary for the purposes set out in this policy. Different
              retention periods apply for different types of personal
              information. When it is no longer necessary to retain your
              personal information, we will delete or anonymize it.
            </p>
          </section>

          <section data-id="element-391">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-392">
              9. California Consumers: Your Rights Under the CCPA/CPRA
            </h3>
            <p className="mb-4" data-id="element-393">
              You have the right under the California Consumer Privacy Act of
              2018 (CCPA), as amended by the California Privacy Rights Act of
              2020 (CPRA), and certain other privacy and data protection laws,
              as applicable, to exercise free of charge:
            </p>

            <h4 className="font-semibold text-teal mt-6 mb-2" data-id="element-394">
              Disclosure of Personal Information We Collect About You
            </h4>
            <p className="mb-2" data-id="element-395">
              You have the right to know, and request disclosure of:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-4" data-id="element-396">
              <li data-id="element-397">
                The categories of personal information we have collected about
                you, including sensitive personal information
              </li>
              <li data-id="element-398">
                The categories of sources from which the personal information is
                collected
              </li>
              <li data-id="element-399">
                Our business or commercial purpose for collecting, selling, or
                sharing personal information
              </li>
              <li data-id="element-400">
                The categories of third parties to whom we disclose personal
                information, if any –and–
              </li>
              <li data-id="element-401">
                The specific pieces of personal information we have collected
                about you
              </li>
            </ul>
            <p className="mb-2" data-id="element-402">Please note that we are not required to:</p>
            <ul className="list-disc pl-5 space-y-1 mb-6" data-id="element-403">
              <li data-id="element-404">
                Retain any personal information about you that was collected for
                a single one-time transaction if, in the ordinary course of
                business, that information about you is not retained
              </li>
              <li data-id="element-405">
                Reidentify or otherwise link any data that, in the ordinary
                course of business, is not maintained in a manner that would be
                considered personal information –or–
              </li>
              <li data-id="element-406">
                Provide the personal information to you more than twice in a
                12-month period
              </li>
            </ul>

            <h4 className="font-semibold text-teal mt-6 mb-2" data-id="element-407">
              Disclosure of Personal Information Sold, Shared, or Disclosed for
              a Business Purpose
            </h4>
            <p className="mb-2" data-id="element-408">
              In connection with any personal information we may sell, share, or
              disclose to a third party for a business purpose, you have the
              right to know:
            </p>
            <ul className="list-disc pl-5 space-y-1 mb-4" data-id="element-409">
              <li data-id="element-410">
                The categories of personal information about you that we sold or
                shared and the categories of third parties to whom the personal
                information was sold or shared –and–
              </li>
              <li data-id="element-411">
                The categories of personal information that we disclosed about
                you for a business purpose and the categories of persons to whom
                the personal information was disclosed for a business purpose
              </li>
            </ul>
            <p className="mb-4" data-id="element-412">
              You have the right to opt-out of the sale of your personal
              information or sharing of your personal information for the
              purpose of targeted behavioral advertising. If you exercise your
              right to opt-out of the sale or sharing of your personal
              information, we will refrain from selling or sharing your personal
              information, unless you subsequently provide express authorization
              for the sale or sharing of your personal information.
            </p>
            <p className="mb-6 italic text-gray-400" data-id="element-413">
              We do not share or sell personal data at any point outside of
              financial institutions. It is up to your financial institution to
              enable the opt out options. This site itself does not collect
              information.
            </p>

            <h4 className="font-semibold text-teal mt-6 mb-2" data-id="element-414">
              Right to Limit Use of Sensitive Personal Information
            </h4>
            <p className="mb-2" data-id="element-415">
              You have the right to limit the use and disclosure of your
              sensitive personal information to the use which is necessary to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-4" data-id="element-416">
              <li data-id="element-417">
                Perform the services or provide the goods reasonably expected by
                an average consumer who requests those goods or services
              </li>
              <li data-id="element-418">
                To perform the following services: (1) Helping to ensure
                security and integrity to the extent the use of the consumer's
                personal information is reasonably necessary and proportionate
                for these purposes; (2) Short-term, transient use, including,
                but not limited to, non-personalized advertising shown as part
                of a consumer's current interaction with the business, provided
                that the consumer's personal information is not disclosed to
                another third party and is not used to build a profile about the
                consumer or otherwise alter the consumer's experience outside
                the current interaction with the business; (3) Performing
                services on behalf of the business, including maintaining or
                servicing accounts, providing customer service, processing or
                fulfilling orders and transactions, verifying customer
                information, processing payments, providing financing, providing
                analytic services, providing storage, or providing similar
                services on behalf of the business; and (4) Undertaking
                activities to verify or maintain the quality or safety of a
                service or device that is owned, manufactured, manufactured for,
                or controlled by the business, and to improve, upgrade, or
                enhance the service or device that is owned, manufactured,
                manufactured for, or controlled by the business –and–
              </li>
              <li data-id="element-419">As authorized by further regulations</li>
            </ul>
            <p className="mb-4" data-id="element-420">
              You have a right to know if your sensitive personal information
              may be used, or disclosed to a service provider or contractor, for
              additional, specified purposes.
            </p>
            <p className="italic text-gray-400" data-id="element-421">
              We do not share or sell personal data at any point outside of
              financial institutions. It is up to your financial institution to
              enable the opt out options. This site itself does not collect
              information.
            </p>
          </section>

          <section data-id="element-422">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-423">
              10. How to Exercise Your Rights
            </h3>
            <p className="mb-4" data-id="element-424">
              If you would like to exercise any of your rights as described in
              this Privacy Policy, you can email us at arthur@fiqis.com or mail
              us at: FIQIS Inc.
            </p>
            <ul className="list-disc pl-5 space-y-2" data-id="element-425">
              <li data-id="element-426">
                Please note that you may only make a CCPA/CPRA-related data
                access or data portability disclosure request twice within a
                12-month period.
              </li>
              <li data-id="element-427">
                If you choose to contact us directly by email/ in writing, you
                will need to provide us with:
                <ul className="list-[circle] pl-5 mt-2 space-y-1" data-id="element-428">
                  <li data-id="element-429">
                    Enough information to identify you (e.g., your full name,
                    address and customer or matter reference number)
                  </li>
                  <li data-id="element-430">
                    Proof of your identity and address (e.g., a copy of your
                    driving license or passport and a recent utility or credit
                    card bill) –and–
                  </li>
                  <li data-id="element-431">
                    A description of what right you want to exercise and the
                    information to which your request relates
                  </li>
                </ul>
              </li>
              <li data-id="element-432">
                We are not obligated to make a data access or data portability
                disclosure if we cannot verify that the person making the
                request is the person about whom we collected information or is
                someone authorized to act on such person's behalf.
              </li>
              <li data-id="element-433">
                Any personal information we collect from you to verify your
                identity in connection with you request will be used solely for
                the purposes of verification.
              </li>
            </ul>
          </section>

          <section data-id="element-434">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-435">
              11. Keeping Your Personal Information Secure
            </h3>
            <p data-id="element-436">
              We have appropriate security measures in place to prevent personal
              information from being accidentally lost or used or accessed in an
              unauthorized way. We limit access to your personal information to
              those who have a genuine business need to access it. Those
              processing your information will do so only in an authorized
              manner and are subject to a duty of confidentiality. We
              continually test our systems and are ISO 27001 certified, which
              means we follow top industry standards for information security.
              We also have procedures in place to deal with any suspected data
              security breach. We will notify you and any applicable regulator
              of a suspected data security breach where we are legally required
              to do so.
            </p>
          </section>

          <section data-id="element-437">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-438">
              12. Changes to This Privacy Notice
            </h3>
            <p className="mb-4" data-id="element-439">
              This privacy notice was published on 03-10-2026 and last updated
              on 03-10-2026.
            </p>
            <p data-id="element-440">
              We may change this privacy notice from time to time– when we do,
              your continued use of the Services after the amended Privacy
              Policy is posted on the Services will constitute your
              acknowledgment and agreement to the amended Privacy Policy.
              However, to the extent the amended Privacy Policy materially
              alters your rights or obligations in this Privacy Policy, the
              amended Privacy Policy will become effective upon the earlier of
              (a) your continued use of the Services with actual knowledge of
              the amended Privacy Policy, or (b) 30 days after the amended
              Privacy Policy is posted on the Services.
            </p>
          </section>

          <section data-id="element-441">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-442">
              13. How to Contact Us
            </h3>
            <p className="mb-4" data-id="element-443">
              If you have any questions comments, or complaints about this
              Privacy Policy, how we collect and use your information, or your
              choices and rights regarding the same, then you may contact us at
              the contact information below. If you are contacting us as an
              authorized agent for another person or business, please also
              provide sufficient information in your request so that we can
              verify that you are authorized to communicate with us as that
              person's or business' authorized agent.
            </p>
            <div className="bg-navy/50 p-4 rounded-lg border border-white/5 space-y-2" data-id="element-444">
              <p data-id="element-445">
                <span className="font-semibold text-teal" data-id="element-446">Phone:</span>{' '}
                480-462-9577
              </p>
              <p data-id="element-447">
                <span className="font-semibold text-teal" data-id="element-448">Email:</span>{' '}
                arthur@fiqis.com
              </p>
              <p data-id="element-449">
                <span className="font-semibold text-teal" data-id="element-450">Address:</span>{' '}
                fiqis.com
              </p>
            </div>
          </section>

          <section data-id="element-451">
            <h3 className="text-xl font-semibold text-white mb-4" data-id="element-452">
              14. Do You Need Extra Help?
            </h3>
            <p data-id="element-453">
              If you would like this notice in another format (for example:
              audio, large print, braille) please contact us (see "How to
              contact us" above).
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 bg-slate/95 backdrop-blur rounded-b-2xl flex justify-end" data-id="element-454">
          <button onClick={onClose} className="bg-teal hover:bg-teal-400 text-navy font-semibold px-6 py-2.5 rounded-md transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,212,170,0.4)]" data-id="element-455">
            Close
          </button>
        </div>
      </div>
    </div>;
}